import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';

const ProjectsContext = createContext();

export function ProjectsProvider({ children }) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [lastFetched, setLastFetched] = useState(0);
    const PROJECTS_PER_PAGE = 9;

    const fetchProjects = useCallback(async (pageIndex = 0, force = false) => {
        // If we already have data and aren't forcing a refresh or loading a new page, return
        // We allow fetching if it's been more than 5 minutes
        const now = Date.now();
        if (!force && pageIndex === 0 && projects.length > 0 && (now - lastFetched < 300000)) {
            setLoading(false);
            return;
        }

        try {
            if (pageIndex === 0) setLoading(true);

            const from = pageIndex * PROJECTS_PER_PAGE;
            const to = from + PROJECTS_PER_PAGE - 1;

            const { data, error } = await supabase
                .from('projects')
                .select('id, name, updated_at, created_at, settings, api_key, variants, ab_test_config, template_id')
                .order('updated_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            if (pageIndex === 0) {
                setProjects(data || []);
            } else {
                setProjects(prev => {
                    // Filter out duplicates just in case
                    const newProjects = data || [];
                    const existingIds = new Set(prev.map(p => p.id));
                    const uniqueNewProjects = newProjects.filter(p => !existingIds.has(p.id));
                    return [...prev, ...uniqueNewProjects];
                });
            }

            setHasMore((data || []).length === PROJECTS_PER_PAGE);
            setPage(pageIndex);
            setLastFetched(now);
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, [projects.length, lastFetched]);

    const addProject = (newProject) => {
        setProjects(prev => [newProject, ...prev]);
    };

    const removeProject = (projectId) => {
        setProjects(prev => prev.filter(p => p.id !== projectId));
    };

    const updateProject = (updatedProject) => {
        setProjects(prev => prev.map(p => p.id === updatedProject.id ? updatedProject : p));
    };

    return (
        <ProjectsContext.Provider
            value={{
                projects,
                loading,
                page,
                hasMore,
                fetchProjects,
                addProject,
                removeProject,
                updateProject
            }}
        >
            {children}
        </ProjectsContext.Provider>
    );
}

export const useProjects = () => {
    const context = useContext(ProjectsContext);
    if (!context) {
        throw new Error('useProjects must be used within ProjectsProvider');
    }
    return context;
};
